/**
 * Mappers for Architecture Health
 *
 * Transforms between:
 *   ArchitectureHealthReport  (UI - camelCase)
 *   ArchitectureHealthRunRow  (DB - snake_case)
 *
 *   violation object          (UI - camelCase)
 *   ArchitectureViolationRow  (DB - snake_case)
 *
 *   HealthRunSummary          (UI - camelCase)
 *   PersistedViolation        (UI - camelCase)
 */

import type { ArchitectureHealthReport } from "@/data/moduleOwnership";
import type {
  ArchitectureHealthRunRow,
  ArchitectureViolationRow,
  HealthRunSummary,
  PersistedViolation,
} from "@/services/architecture-health.service";

// ── ArchitectureHealthReport: UI → DB ─────────────────────────────────────────

export function toDbHealthRun(
  report: ArchitectureHealthReport,
): Omit<ArchitectureHealthRunRow, "id" | "created_at"> {
  const passedChecks = report.checks.filter(c => c.passed).length;
  return {
    ran_at: new Date().toISOString(),
    healthy: report.healthy,
    total_checks: report.checks.length,
    passed_checks: passedChecks,
    failed_checks: report.checks.length - passedChecks,
    violation_count: report.violations.length,
  };
}

// ── ArchitectureHealthRunRow: DB → UI ─────────────────────────────────────────

export function toUiHealthRun(row: ArchitectureHealthRunRow): HealthRunSummary {
  return {
    id: row.id,
    ranAt: row.ran_at,
    healthy: row.healthy,
    totalChecks: row.total_checks,
    passedChecks: row.passed_checks,
    failedChecks: row.failed_checks,
    violationCount: row.violation_count,
  };
}

// ── Violation: UI → DB ────────────────────────────────────────────────────────

export function toDbViolation(
  v: { timestamp: string; caller: string; domain: string; operation: string },
): Omit<ArchitectureViolationRow, "id" | "created_at"> {
  return {
    timestamp: v.timestamp,
    caller: v.caller,
    domain: v.domain,
    operation: v.operation,
    cleared: false,
  };
}

// ── ArchitectureViolationRow: DB → UI ─────────────────────────────────────────

export function toUiViolation(row: ArchitectureViolationRow): PersistedViolation {
  return {
    id: row.id,
    timestamp: row.timestamp,
    caller: row.caller,
    domain: row.domain,
    operation: row.operation,
    cleared: row.cleared,
  };
}
